import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { buildMultiQuoteHttpOptions } from '@common';
import { CONFIG } from '@config';
import { map, Observable, tap } from 'rxjs';
import { GetAppliancesForBrandResponse } from '../models/get-appliances-for-brand-response.interface';
import { GetBrandsByApplianceCodeResponse } from '../models/get-brands-by-appliance-code-response.interface';
import { ProgressiveFormApplianceType } from '../models/progressive-form-appliance-type.interface';

@Injectable({ providedIn: 'root' })
export class CatalogueService {
  private readonly config = inject(CONFIG);
  private readonly httpClient = inject(HttpClient);
  private readonly catalogueEndpoint = this.config.catalogueApi;

  private readonly _steppedFormAppliance = signal<
    ProgressiveFormApplianceType | undefined
  >(undefined);
  private readonly _steppedFormApplianceWarranty = signal<number>(12);
  private readonly _selectedAppliancePurchasePrice = signal<number>(0);
  private readonly _selectedAppliancePurchaseDate = signal<Date>(new Date());
  private readonly _selectedApplianceModelNumber = signal<string>('');
  private readonly _selectedApplianceSerialNumber = signal<string>('');

  private _appliancesForBrandResponse =
    signal<GetAppliancesForBrandResponse | null>(null);
  readonly _appliancesForBrandIsLoading = signal<boolean>(false);

  // ===== Public signals =====
  readonly lowRiskPurchasePrice = computed(
    () => this._selectedAppliancePurchasePrice() <= 750,
  );

  readonly warrantyLengthInYears = computed(() => {
    const months = this._steppedFormApplianceWarranty();
    const years = months / 12;
    return years === 1 ? '1 year' : `${years} years`;
  });

  readonly steppedFormAppliance = this._steppedFormAppliance.asReadonly();
  readonly steppedFormApplianceWarranty =
    this._steppedFormApplianceWarranty.asReadonly();
  readonly selectedAppliancePurchasePrice =
    this._selectedAppliancePurchasePrice.asReadonly();
  readonly selectedAppliancePurchaseDate =
    this._selectedAppliancePurchaseDate.asReadonly();
  readonly selectedApplianceModelNumber =
    this._selectedApplianceModelNumber.asReadonly();
  readonly selectedApplianceSerialNumber =
    this._selectedApplianceSerialNumber.asReadonly();
  readonly appliancesForBrandResponse =
    this._appliancesForBrandResponse.asReadonly();

  // ===== Mutators =====

  setSteppedFormAppliance(appliance: ProgressiveFormApplianceType | undefined) {
    this._steppedFormAppliance.set(appliance);
  }

  setSteppedFormApplianceWarranty(warranty: number) {
    this._steppedFormApplianceWarranty.set(warranty);
  }

  setSelectedAppliancePurchasePrice(price: number) {
    this._selectedAppliancePurchasePrice.set(price);
  }

  setSelectedAppliancePurchaseDate(date: Date) {
    this._selectedAppliancePurchaseDate.set(date);
  }

  setSelectedApplianceModelNumber(modelNumber: string) {
    this._selectedApplianceModelNumber.set(modelNumber);
  }

  setSelectedApplianceSerialNumber(serialNumber: string) {
    this._selectedApplianceSerialNumber.set(serialNumber);
  }

  getBrandsByApplianceCode(
    applianceCode: string,
  ): Observable<GetBrandsByApplianceCodeResponse[]> {
    const options = buildMultiQuoteHttpOptions(true);

    return this.httpClient
      .get<{
        result: GetBrandsByApplianceCodeResponse[];
      }>(
        `${this.catalogueEndpoint}/v1/appliances/${applianceCode}/brands`,
        options,
      )
      .pipe(map((response) => response.result));
  }

  getAppliancesForBrand(): Observable<GetAppliancesForBrandResponse> {
    this._appliancesForBrandIsLoading.set(true);

    return this.httpClient
      .get<{
        result: GetAppliancesForBrandResponse;
      }>(
        `${this.catalogueEndpoint}/v1/catalogue/${this.config.clientBrandCode}`,
      )
      .pipe(
        map((response) => response.result),
        tap((result) => {
          this._appliancesForBrandResponse.set(result);
          this._appliancesForBrandIsLoading.set(false);
        }),
      );
  }
}
